import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, PhotoIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import Cropper, { Area } from 'react-easy-crop';
import { useToast } from '../context/ToastContext';

interface SEOData {
  var_title: string;
  var_description: string;
  var_keywords: string;
  var_og_description: string;
  var_site_name: string;
  var_twitter_description: string;
  var_og_image_url?: string;
}

const SEOSettings: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [seoData, setSeoData] = useState<SEOData>({
    var_title: '',
    var_description: '',
    var_keywords: '',
    var_og_description: '',
    var_site_name: '',
    var_twitter_description: ''
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Image upload states
  const [showImageModal, setShowImageModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [existingImagesLoading, setExistingImagesLoading] = useState(false);
  const [existingImagesError, setExistingImagesError] = useState('');
  const [deletingImage, setDeletingImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [pendingImageName, setPendingImageName] = useState<string>('');
  const [pendingImageMime, setPendingImageMime] = useState<string>('image/jpeg');
  const [rotation, setRotation] = useState(0);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleImageError = (url: string) => {
    setImageErrors(prev => new Set(prev).add(url));
  };

  useEffect(() => {
    loadSEOData();
  }, []);

  // Load existing images when modal opens
  useEffect(() => {
    if (showImageModal) {
      loadExistingImages();
    }
  }, [showImageModal]);


  const loadSEOData = () => {
    setLoading(true);
    try {
      // Load from localStorage
      const storedDetails = localStorage.getItem('restaurantDetails');
      if (storedDetails) {
        const parsed = JSON.parse(storedDetails);
        if (parsed.seo) {
          setSeoData({
            var_title: parsed.seo.var_title || '',
            var_description: parsed.seo.var_description || '',
            var_keywords: parsed.seo.var_keywords || '',
            var_og_description: parsed.seo.var_og_description || '',
            var_site_name: parsed.seo.var_site_name || '',
            var_twitter_description: parsed.seo.var_twitter_description || '',
            var_og_image_url: parsed.seo.var_og_image_url || ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading SEO data:', error);
      showError('Error', 'Error loading SEO settings');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingImages = async () => {
    setExistingImagesLoading(true);
    setExistingImagesError('');
    try {
      const response = await apiService.getImages('og_image');
      if (response.status && response.data) {
        setExistingImages(response.data.map(img => img.path));
      } else {
        setExistingImagesError('Failed to load images');
      }
    } catch (error) {
      setExistingImagesError('Error loading images');
    } finally {
      setExistingImagesLoading(false);
    }
  };

  const readFileAsDataURL = useCallback((file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, []);

  const createImage = useCallback((url: string) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });
  }, []);

  const getRadianAngle = (degreeValue: number) => (degreeValue * Math.PI) / 180;

  const rotateSize = (width: number, height: number, rotationValue: number) => {
    const rotRad = getRadianAngle(rotationValue);

    return {
      width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
      height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    };
  };

  const getCroppedBlob = useCallback(
    async (imageSrc: string, pixelCrop: Area, mimeType: string, rotationValue: number) => {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to create canvas context');
      }

      const rotRad = getRadianAngle(rotationValue);
      const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotationValue);

      canvas.width = bBoxWidth;
      canvas.height = bBoxHeight;

      ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
      ctx.rotate(rotRad);
      ctx.translate(-image.width / 2, -image.height / 2);
      ctx.drawImage(image, 0, 0);

      const croppedCanvas = document.createElement('canvas');
      const croppedCtx = croppedCanvas.getContext('2d');

      if (!croppedCtx) {
        throw new Error('Failed to create cropped canvas context');
      }

      croppedCanvas.width = pixelCrop.width;
      croppedCanvas.height = pixelCrop.height;

      croppedCtx.drawImage(
        canvas,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      return new Promise<Blob>((resolve, reject) => {
        croppedCanvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            resolve(blob);
          },
          mimeType === 'image/png' ? 'image/png' : 'image/jpeg',
          mimeType === 'image/png' ? undefined : 0.92
        );
      });
    },
    [createImage]
  );

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await readFileAsDataURL(file);
      setCropImageSrc(dataUrl);
      setShowCropper(true);
      setPendingImageName(file.name || 'og_image.jpg');
      setPendingImageMime(file.type || 'image/jpeg');
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setRotation(0);
    } catch (err) {
      console.error('Image preview error:', err);
      showError('Error', 'Failed to load image for cropping. Please try again.');
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixelsValue: Area) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const resetCropState = useCallback(() => {
    setShowCropper(false);
    setCropImageSrc(null);
    setCroppedAreaPixels(null);
    setPendingImageName('');
    setPendingImageMime('image/jpeg');
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setRotation(0);
  }, []);

  const handleCropCancel = () => {
    resetCropState();
  };

  const handleCropConfirm = async () => {
    if (!cropImageSrc || !croppedAreaPixels) {
      return;
    }

    setUploadingImage(true);
    try {
      const croppedBlob = await getCroppedBlob(cropImageSrc, croppedAreaPixels, pendingImageMime, rotation);
      const fileExtension = pendingImageMime === 'image/png' ? 'png' : 'jpg';
      const safeFileName = pendingImageName || `og_image.${fileExtension}`;
      const croppedFile = new File([croppedBlob], safeFileName, { type: croppedBlob.type });

      const result = await apiService.uploadImage(croppedFile, 'og_image');

      if (result.status && result.data) {
        const updatedSeoData = {
          ...seoData,
          var_og_image_url: result.data!.path
        };
        setSeoData(updatedSeoData);
        
        // Update localStorage
        const storedDetails = localStorage.getItem('restaurantDetails');
        if (storedDetails) {
          const parsed = JSON.parse(storedDetails);
          const updatedDetails = {
            ...parsed,
            seo: {
              ...parsed.seo,
              var_og_image_url: result.data!.path
            }
          };
          localStorage.setItem('restaurantDetails', JSON.stringify(updatedDetails));
        }
        
        // Update via API
        try {
          const updateResponse = await apiService.updateRestaurantProfileSEO(updatedSeoData);
          if (updateResponse.status) {
            showSuccess('Success', 'OG Image updated successfully!');
          } else {
            showError('Error', updateResponse.message || 'Failed to update OG image');
          }
        } catch (error) {
          console.error('Error updating SEO:', error);
          showError('Error', 'Image uploaded but failed to update SEO. Please try again.');
        }
        
        await loadExistingImages();
        setShowImageModal(false);
        resetCropState();
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      showError('Error', 'Failed to upload cropped image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageSelect = async (imageUrl: string) => {
    const updatedSeoData = {
      ...seoData,
      var_og_image_url: imageUrl
    };
    setSeoData(updatedSeoData);
    
    // Update localStorage
    const storedDetails = localStorage.getItem('restaurantDetails');
    if (storedDetails) {
      const parsed = JSON.parse(storedDetails);
      const updatedDetails = {
        ...parsed,
        seo: {
          ...parsed.seo,
          var_og_image_url: imageUrl
        }
      };
      localStorage.setItem('restaurantDetails', JSON.stringify(updatedDetails));
    }
    
    // Update via API
    try {
      const updateResponse = await apiService.updateRestaurantProfileSEO(updatedSeoData);
      if (updateResponse.status) {
        showSuccess('Success', 'OG Image updated successfully!');
      } else {
        showError('Error', updateResponse.message || 'Failed to update OG image');
      }
    } catch (error) {
      console.error('Error updating SEO:', error);
      showError('Error', 'Failed to update OG image. Please try again.');
    }
    
    setShowImageModal(false);
  };

  const handleImageDelete = async (imageUrl: string) => {
    if (!confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return;
    }

    setDeletingImage(imageUrl);
    try {
      const result = await apiService.deleteImage(imageUrl);
      if (result.status) {
        setExistingImages(prev => prev.filter(img => img !== imageUrl));
        // If the deleted image was being used, clear it
        if (seoData.var_og_image_url === imageUrl) {
          const updatedSeoData = { ...seoData, var_og_image_url: '' };
          setSeoData(updatedSeoData);
          
          // Update localStorage
          const storedDetails = localStorage.getItem('restaurantDetails');
          if (storedDetails) {
            const parsed = JSON.parse(storedDetails);
            const updatedDetails = {
              ...parsed,
              seo: {
                ...parsed.seo,
                var_og_image_url: ''
              }
            };
            localStorage.setItem('restaurantDetails', JSON.stringify(updatedDetails));
          }
        }
        showSuccess('Success', 'Image deleted successfully');
      } else {
        showError('Error', 'Failed to delete image');
      }
    } catch (error) {
      showError('Error', 'Error deleting image');
    } finally {
      setDeletingImage(null);
    }
  };

  const openImageModal = () => {
    setShowImageModal(true);
  };


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSeoData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Update via API with seo key (including var_og_image_url)
      const response = await apiService.updateRestaurantProfileSEO(seoData);
      
      if (response.status) {
        // Update localStorage
        const storedDetails = localStorage.getItem('restaurantDetails');
        if (storedDetails) {
          const parsed = JSON.parse(storedDetails);
          const updatedDetails = {
            ...parsed,
            seo: seoData
          };
          localStorage.setItem('restaurantDetails', JSON.stringify(updatedDetails));
        }
        
        showSuccess('Success', 'SEO settings updated successfully!');
      } else {
        const errorMessage = response.message || 'Failed to update SEO settings';
        showError('Error', errorMessage);
      }
    } catch (error) {
      const errorMessage = 'Error updating SEO settings';
      showError('Error', errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900">SEO Settings</h1>
        <p className="text-sm text-gray-600">Optimize your store for search engines</p>
      </div> */}

      <div className="bg-white rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic SEO Information */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Basic SEO</h2>
            
            <div>
              <label htmlFor="var_title" className="block text-sm font-medium text-gray-700 mb-2">
                Page Title *
              </label>
              <input
                type="text"
                id="var_title"
                name="var_title"
                value={seoData.var_title}
                onChange={handleInputChange}
                required
                maxLength={60}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                placeholder="Enter your page title here"
              />
              <p className="mt-1 text-sm text-gray-500">
                The title that appears in search engine results (recommended: 50-60 characters)
              </p>
            </div>

            <div>
              <label htmlFor="var_description" className="block text-sm font-medium text-gray-700 mb-2">
                Meta Description *
              </label>
              <textarea
                id="var_description"
                name="var_description"
                value={seoData.var_description}
                onChange={handleInputChange}
                required
                rows={3}
                maxLength={160}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                placeholder="Enter your meta description here"
              />
              <p className="mt-1 text-sm text-gray-500">
                A brief description of your store (recommended: 150-160 characters)
              </p>
            </div>

            <div>
              <label htmlFor="var_keywords" className="block text-sm font-medium text-gray-700 mb-2">
                Keywords
              </label>
              <input
                type="text"
                id="var_keywords"
                name="var_keywords"
                value={seoData.var_keywords}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                placeholder="e.g., restaurant, cafe, food menu, tasty dishes, fresh meals"
              />
              <p className="mt-1 text-sm text-gray-500">
                Comma-separated keywords relevant to your store
              </p>
            </div>
          </div>

          {/* Open Graph (Social Media) */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Social Media (Open Graph)</h2>
            
            <div>
              <label htmlFor="var_site_name" className="block text-sm font-medium text-gray-700 mb-2">
                Site Name *
              </label>
              <input
                type="text"
                id="var_site_name"
                name="var_site_name"
                value={seoData.var_site_name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                placeholder="Enter your site name here"
              />
              <p className="mt-1 text-sm text-gray-500">
                The name of your site as it appears when shared on social media
              </p>
            </div>

            <div>
              <label htmlFor="var_og_description" className="block text-sm font-medium text-gray-700 mb-2">
                Open Graph Description *
              </label>
              <textarea
                id="var_og_description"
                name="var_og_description"
                value={seoData.var_og_description}
                onChange={handleInputChange}
                required
                rows={3}
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                placeholder="Enter your open graph description here"
              />
              <p className="mt-1 text-sm text-gray-500">
                Description shown when your store is shared on Facebook, LinkedIn, etc.
              </p>
            </div>

            {/* OG Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Open Graph Image
              </label>
              <div className="space-y-3">
                {/* Image Preview */}
                {seoData.var_og_image_url && (
                  <div className="relative inline-block group">
                    {imageErrors.has(seoData.var_og_image_url) ? (
                      <div className="w-full max-w-md h-48 flex items-center justify-center bg-gray-200 rounded-lg border border-gray-300">
                        <span className="text-sm text-gray-500">Image not found</span>
                      </div>
                    ) : (
                      <img
                        src={seoData.var_og_image_url}
                        alt="OG Image"
                        onError={() => handleImageError(seoData.var_og_image_url!)}
                        className="w-full max-w-md h-auto object-cover rounded-lg border border-gray-300 bg-gray-50"
                      />
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        const updatedSeoData = { ...seoData, var_og_image_url: '' };
                        setSeoData(updatedSeoData);
                        
                        // Update localStorage
                        const storedDetails = localStorage.getItem('restaurantDetails');
                        if (storedDetails) {
                          const parsed = JSON.parse(storedDetails);
                          const updatedDetails = {
                            ...parsed,
                            seo: {
                              ...parsed.seo,
                              var_og_image_url: ''
                            }
                          };
                          localStorage.setItem('restaurantDetails', JSON.stringify(updatedDetails));
                        }
                        
                        // Update via API
                        try {
                          const updateResponse = await apiService.updateRestaurantProfileSEO(updatedSeoData);
                          if (updateResponse.status) {
                            showSuccess('Success', 'OG Image removed successfully!');
                          } else {
                            showError('Error', updateResponse.message || 'Failed to remove OG image');
                          }
                        } catch (error) {
                          console.error('Error updating SEO:', error);
                          showError('Error', 'Failed to remove OG image. Please try again.');
                        }
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      title="Remove OG image"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
                
                {/* Upload Button */}
                <button
                  type="button"
                  onClick={openImageModal}
                  className="flex items-center px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                >
                  <PhotoIcon className="h-4 w-4 mr-2" />
                  {seoData.var_og_image_url ? 'Change OG Image' : 'Upload OG Image'}
                </button>
                <p className="text-sm text-gray-500">
                  Recommended size: 1200x630px (1.91:1 ratio). This image appears when your store is shared on social media.
                </p>
              </div>
            </div>
          </div>

          {/* Twitter */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Twitter</h2>
            
            <div>
              <label htmlFor="var_twitter_description" className="block text-sm font-medium text-gray-700 mb-2">
                Twitter Description *
              </label>
              <textarea
                id="var_twitter_description"
                name="var_twitter_description"
                value={seoData.var_twitter_description}
                onChange={handleInputChange}
                required
                rows={3}
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                placeholder="Enter your twitter description here"
              />
              <p className="mt-1 text-sm text-gray-500">
                Description shown when your store is shared on Twitter/X
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6 border-t">
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white font-medium rounded-lg hover:from-orange-600 hover:to-pink-700 focus:ring-2 focus:ring-pink-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Update SEO Settings'}
            </button>
          </div>
        </form>
      </div>

      {/* Image Upload Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 !mt-0">
          <div onClick={() => setShowImageModal(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Select or Upload OG Image
                </h3>
                <button 
                  onClick={() => setShowImageModal(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              <div>
                {/* Upload Section */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Upload New Image</h4>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {uploadingImage ? (
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                        ) : (
                          <>
                            <PlusIcon className="w-8 h-8 mb-2 text-gray-500" />
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG or JPEG (MAX. 5MB)</p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                    </label>
                  </div>
                </div>

                {/* Existing Images Section */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Choose from Existing Images</h4>
                  
                  {existingImagesLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading images...</span>
                    </div>
                  ) : existingImagesError ? (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{existingImagesError}</p>
                    </div>
                  ) : existingImages.length === 0 ? (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-500">No existing images found. Upload a new image to get started.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {existingImages.map((image, index) => (
                        <div
                          key={index}
                          className="relative rounded-lg overflow-hidden border-2 border-gray-300 hover:border-gray-400 cursor-pointer group"
                          onClick={() => handleImageSelect(image)}
                        >
                          {imageErrors.has(image) ? (
                            <div className="w-full h-20 flex items-center justify-center bg-gray-200">
                              <span className="text-xs text-gray-500">Image not found</span>
                            </div>
                          ) : (
                            <img
                              src={image}
                              alt={`Option ${index + 1}`}
                              onError={() => handleImageError(image)}
                              className="w-full h-20 object-cover"
                            />
                          )}
                          
                          {/* Selection indicator */}
                          {seoData.var_og_image_url === image ? (
                            <div className="absolute inset-0 bg-orange-500 bg-opacity-20 flex items-center justify-center">
                              <div className="bg-orange-500 text-white rounded-full p-1">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          ) : null}
                          
                          {/* Delete button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageDelete(image);
                            }}
                            disabled={deletingImage === image}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 group-hover:opacity-100 transition-opacity hover:bg-red-600 disabled:opacity-50"
                            title="Delete image"
                          >
                            {deletingImage === image ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            ) : (
                              <TrashIcon className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Cropper Modal */}
      {showCropper && cropImageSrc && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">
                Crop OG Image
              </h3>
              <button
                onClick={handleCropCancel}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="relative w-full h-72 sm:h-96 bg-gray-900 flex-shrink-0">
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1.91}
                cropShape="rect"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                objectFit="contain"
              />
            </div>
            <div className="px-4 py-4 border-t flex-shrink-0">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4 w-full">
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-sm text-gray-600 whitespace-nowrap min-w-[50px]">Zoom</span>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(event) => setZoom(Number(event.target.value))}
                      className="w-full accent-orange-600"
                    />
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-sm text-gray-600 whitespace-nowrap min-w-[70px]">Rotation</span>
                    <input
                      type="range"
                      min={-180}
                      max={180}
                      step={1}
                      value={rotation}
                      onChange={(event) => setRotation(Number(event.target.value))}
                      className="w-full accent-orange-600"
                    />
                    <span className="text-sm text-gray-800 w-12 text-right">{`${rotation}°`}</span>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2 border-t">
                  <button
                    onClick={handleCropCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                    disabled={uploadingImage}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? 'Uploading...' : 'Save Crop'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SEOSettings;

